import { Box, Text, Hidden } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { find, pathOr, pipe, propEq, values, prop, gt, __ } from 'ramda'
import { useGetUsages } from '@/hooks/use-block-and-pay'
import useProfile from '@/hooks/use-profile'
import { WrapperStyle } from './index.css'
import Empty from './Empty'
import ALaCarte from './ALaCarte'
import PlanProducts from './PlanProducts'
import Skeleton from '../Skeletons'
import BaseLayout from '../../layout/base-layout'
import { PlanType } from '../../types'

const MyProducts = () => {
  const { t } = useTranslation()
  const profile = useProfile()

  const currentPlan = profile?.company_info?.base_plan_name as PlanType
  const isNotEssentials = currentPlan !== 'essentials-plan-monthly'
  const { data, isLoading } = useGetUsages()

  const hasPurchasedCreditChecks: boolean = pipe(
    pathOr([], ['blocks']),
    values as any,
    find(propEq('name', 'credit_checks_additional_packages')),
    prop('value') as any,
    gt(__, 0)
  )(data)

  const hasPurchasedSponsoredListing: boolean = pipe(
    pathOr([], ['blocks']),
    values as any,
    find(propEq('name', 'sponsored_listing_days')),
    prop('value') as any,
    gt(__, 0)
  )(data)

  const hasPurchasedSyndications: boolean = pipe(
    pathOr([], ['blocks']),
    values as any,
    find(propEq('name', 'syndication_additional_packages')),
    prop('value') as any,
    gt(__, 0)
  )(data)

  const purchasedCreditChecks =
    data?.blocks?.credit_checks_additional_packages?.current_value -
    data?.blocks?.credit_checks_additional_packages?.usage?.current_value
  const purchasedSponsoredListings =
    data?.blocks?.sponsored_listing_days?.value -
    data?.blocks?.sponsored_listing_days?.usage?.current_value
  const purchasedSyndications =
    data?.blocks?.syndication_additional_packages?.value -
    data?.blocks?.syndication_additional_packages?.usage?.current_value

  return (
    <>
      <BaseLayout title={t('b.usage.credits.title', 'Credit Usage')}>
        <Box
          className={WrapperStyle}
          paddingY={{ mobile: 'gutter', desktop: 'xlarge' }}
          paddingX={{ mobile: 'gutter', desktop: 'gutter', wide: 'xxxlarge' }}
          flexDirection="column"
          display="flex"
          gap="large"
        >
          <Box flexDirection="column" display="flex" gap="xxsmall" maxWidth="large">
            <Hidden below="tablet">
              <Text size="large" weight="strong" tone="neutral" baseline={false}>
                {t('b.usage.credits.my_products.text', 'My Products')}
              </Text>
            </Hidden>
            <Text tone="secondary" baseline={false} size="small">
              {t(
                'b.usage.credits.my_products.subtitle',
                'Easily unlock additional features for your listings, ready to use whenever you need them.'
              )}
            </Text>
          </Box>
          {isNotEssentials && <PlanProducts currentPlan={currentPlan} />}
          {isLoading ? (
            <Skeleton id="my-products" />
          ) : hasPurchasedCreditChecks ||
            hasPurchasedSponsoredListing ||
            hasPurchasedSyndications ? (
            <ALaCarte
              hasPurchasedCreditChecks={hasPurchasedCreditChecks}
              hasPurchasedSponsoredListing={hasPurchasedSponsoredListing}
              hasPurchasedSyndications={hasPurchasedSyndications}
              purchasedCredits={{
                creditChecks: purchasedCreditChecks,
                sponsoredListings: purchasedSponsoredListings,
                syndications: purchasedSyndications
              }}
            />
          ) : (
            <Empty isNotEssentials={isNotEssentials} />
          )}
        </Box>
      </BaseLayout>
    </>
  )
}

export default MyProducts
